import { motion } from "framer-motion";

const LEAD_LINES = [
  "A senior in Greenville.",
  "A family in Wake County.",
  "A waitlist that never gets shorter.",
];

const STATS = [
  { value: "18", label: "Communities" },
  { value: "2,500+", label: "Residents Served" },
  { value: "17", label: "Counties Across NC & SC" },
  { value: "2", label: "States" },
];

const PARTNERS = [
  ["U.S. Dept. of HUD", "Program & Rental Assistance"],
  ["Walker & Dunlop", "Permanent Financing"],
  ["Berkadia", "Agency & Bridge Financing"],
  ["National Urban League", "Non-Profit Partner"],
  ["Tredway", "Development & Capital Partner"],
  ["CBRE", "Advisory"],
  ["Freddie Mac", "Financing"],
  ["Beacon Management", "Property Management"],
  ["Asset Living", "Construction Management"],
];

const fadeUp = {
  hidden: { opacity: 0, y: 40 },
  visible: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.8, ease: [0.16, 1, 0.3, 1], delay: i * 0.08 },
  }),
};

const reveal = {
  hidden: { opacity: 0, y: 40 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.8, ease: [0.16, 1, 0.3, 1] } },
};

export const About = () => {
  return (
    <section id="about" data-testid="about-section" className="relative bg-[#F7F5F0] py-24 md:py-36">
      <div className="mx-auto max-w-[1400px] px-6 md:px-12">
        {/* Intro */}
        <div className="grid md:grid-cols-12 gap-y-10 md:gap-12 items-end mb-20 md:mb-28">
          <div className="md:col-span-7">
            <motion.p variants={reveal} initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-80px" }}
              className="text-xs uppercase tracking-[0.3em] text-[#C56A4E] mb-6">
              Company Overview
            </motion.p>
            <motion.h2 variants={reveal} initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-80px" }}
              className="font-heading text-4xl md:text-5xl lg:text-6xl leading-[1.02] tracking-tight text-[#1C2220]">
              Preservation, not just <span className="italic text-[#C56A4E]">ownership.</span>
            </motion.h2>
          </div>
          <motion.div variants={reveal} initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-80px" }}
            className="md:col-span-5 md:col-start-9">
            <p className="text-base md:text-lg leading-relaxed text-[#1C2220]/70">
              Formed in October 2019 with a single mission — to preserve and develop
              affordable housing while delivering risk-adjusted returns to our capital
              partners. Today we own, preserve, and oversee 18 communities that are home
              to more than 2,500 residents across North Carolina and South Carolina.
            </p>
          </motion.div>
        </div>

        {/* Mission pull-quote */}
        <motion.blockquote variants={reveal} initial="hidden" whileInView="visible" viewport={{ once: true }}
          data-testid="mission-quote"
          className="border-y border-[#1C2220]/15 py-14 md:py-20 my-4">
          <p className="font-heading text-2xl md:text-4xl lg:text-5xl leading-[1.15] tracking-tight text-[#1C2220] max-w-5xl">
            “Preserving the homes people can afford, for the residents who need them
            most — <span className="italic text-[#C56A4E]">today</span>, and for
            generations to come.”
          </p>
        </motion.blockquote>

        {/* How we work — editorial narrative */}
        <div className="mt-20 md:mt-28" data-testid="how-we-work">
          <p className="text-xs uppercase tracking-[0.3em] text-[#C56A4E] mb-10">How We Work</p>

          <div className="grid md:grid-cols-12 gap-10 md:gap-12">
            {/* Punchy lead */}
            <div className="md:col-span-5">
              <div className="md:sticky md:top-28">
                {LEAD_LINES.map((line, i) => (
                  <motion.p
                    key={line}
                    variants={fadeUp}
                    custom={i}
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true, margin: "-60px" }}
                    className="font-heading text-3xl md:text-4xl lg:text-[2.75rem] leading-[1.08] tracking-tight text-[#1C2220]"
                  >
                    {line}
                  </motion.p>
                ))}
                <motion.p
                  variants={fadeUp}
                  custom={3}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true }}
                  className="mt-6 font-heading italic text-2xl md:text-3xl text-[#C56A4E]"
                >
                  These are the people and communities we keep in mind with every investment.
                </motion.p>
              </div>
            </div>

            {/* Narrative body */}
            <motion.div
              variants={reveal}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-60px" }}
              className="md:col-span-6 md:col-start-7 space-y-6 text-base md:text-lg leading-relaxed text-[#1C2220]/75"
            >
              <p>
                Behind every acquisition is contract rent, subsidy compliance, and physical
                condition data — the same discipline any serious investor would demand. But
                behind that data is someone's home. A resident at Gandy Allmon Manor who's lived
                there for a decade. A family in Wake County searching for a quality home they
                can afford in the community they already call home.
              </p>
              <p>
                We approach every acquisition with institutional discipline, rigorous
                underwriting, and careful due diligence. Our responsibility does not end at
                closing. We remain actively involved through long-term ownership oversight,
                capital planning, compliance, and close coordination with experienced
                third-party property-management partners.
              </p>
            </motion.div>
          </div>

          {/* Manifesto close */}
          <motion.p
            variants={reveal}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-80px" }}
            className="mt-16 md:mt-24 font-heading text-3xl md:text-4xl lg:text-5xl leading-[1.12] tracking-tight text-[#1C2220] max-w-4xl"
          >
            We don't flip these communities. We don't build them to sell. Affordable isn't a
            phase of ownership for us. It's <span className="italic text-[#C56A4E]">the whole point.</span>
          </motion.p>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-y-12 gap-x-8 mt-20 md:mt-28">
          {STATS.map((s, i) => (
            <motion.div key={s.label} variants={fadeUp} custom={i} initial="hidden" whileInView="visible"
              viewport={{ once: true }} data-testid={`stat-${i}`}>
              <div className="font-heading text-5xl md:text-6xl text-[#1C2220] tracking-tight">{s.value}</div>
              <div className="mt-3 text-xs uppercase tracking-[0.2em] text-[#1C2220]/50">{s.label}</div>
            </motion.div>
          ))}
        </div>

        {/* Origin story */}
        <motion.div variants={reveal} initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-80px" }}
          data-testid="origin-story" className="mt-24 md:mt-32 grid md:grid-cols-12 gap-8 md:gap-12">
          <div className="md:col-span-4">
            <p className="text-xs uppercase tracking-[0.3em] text-[#C56A4E] mb-5">Our Origin Story</p>
            <h3 className="font-heading text-3xl md:text-4xl leading-tight text-[#1C2220]">
              How we started — the NC HAP portfolio
            </h3>
          </div>
          <div className="md:col-span-7 md:col-start-6">
            <p className="text-base md:text-lg leading-relaxed text-[#1C2220]/75">
              In October 2019, our team acquired a foreclosed portfolio of HAP properties
              from HUD's Property Disposition office in Fort Worth, TX. Originally developed
              by a non-profit under the Section 202 program for the elderly and disabled, the
              properties had fallen into disrepair after the founder passed away and HUD
              foreclosed directly. We were selected through a public auction and detailed
              review process — and the portfolio has since been recapitalized into a single
              HUD 223(f) scattered-site loan. It is the foundation the firm was built on.
            </p>
            <ul className="mt-8 grid sm:grid-cols-2 gap-x-8 gap-y-3 text-sm text-[#1C2220]/70">
              {[
                "Public RFP / foreclosure acquisition",
                "Closed 52 days after bid approval",
                "Bridge loan to HUD 223(f)",
                "HUD-required rehab rider",
                "20-year low-income use agreement",
                "Preserved for elderly residents 62+",
              ].map((d) => (
                <li key={d} className="flex gap-3 border-b border-[#1C2220]/10 pb-3">
                  <span className="text-[#C56A4E]">—</span> {d}
                </li>
              ))}
            </ul>
          </div>
        </motion.div>

        {/* Partners */}
        <div className="mt-24 md:mt-32">
          <p className="text-xs uppercase tracking-[0.3em] text-[#1C2220]/45 mb-8">Our Partners</p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-px bg-[#1C2220]/10 border border-[#1C2220]/10">
            {PARTNERS.map(([name, role]) => (
              <div key={name} className="bg-[#F7F5F0] p-6 md:p-7">
                <div className="font-heading text-xl text-[#1C2220]">{name}</div>
                <div className="mt-1 text-xs uppercase tracking-[0.18em] text-[#1C2220]/45">{role}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default About;
